// ./src/utils/api/ReceiptIssuerService.ts
//
// ROADMAP #204 — who signs a donation receipt: the ACCOUNT's details (organizerProfileId null) and, per organizer page, an
// optional override (owner, 2026-10-31). The API's ReceiptIssuerCore owns every rule (one per page; a page that is
// another charity numbers its own series; details that numbered a receipt cannot be removed) and reads back `missing` —
// the fields the regime still needs before official receipts can be given. OpResult-native.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { listAllPages } from "./listAllPages";

// Mirrors eventsmagic-api boundary/donation.ts RECEIPT_REGIME.
export const RECEIPT_REGIME_VALUES = ["ca_cra", "us_irs", "plain"] as const;
export type ReceiptRegime = (typeof RECEIPT_REGIME_VALUES)[number];

const FIELDS = `
  id
  accountId
  organizerProfileId
  regime
  legalName
  registrationNumber
  streetAddress
  extraAddress
  city
  stateProvince
  postalCode
  country
  issueLocation
  signatoryName
  signatoryTitle
  signatureUploadedFileId
  signatureUploadedFile { id fileUrl thumbnailUrl }
  receiptPrefix
  ownSeries
  footerText
  missing
  status
  rights
  createdAt
  updatedAt
`;

const ERRORS = `errors { name errors warnings }`;

const LIST_QUERY = gql`
  query ReceiptIssuerList($filter: ReceiptIssuerFilter, $params: PaginationAndSorting) {
    receiptIssuerList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      ${ERRORS}
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation ReceiptIssuerCreate($params: ReceiptIssuerInput) {
    receiptIssuerCreate(params: $params) {
      code
      data { ${FIELDS} }
      ${ERRORS}
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation ReceiptIssuerUpdate($where: ReceiptIssuerWhereInput, $params: ReceiptIssuerInput) {
    receiptIssuerUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      ${ERRORS}
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation ReceiptIssuerRemove($where: ReceiptIssuerWhereInput) {
    receiptIssuerRemove(where: $where) {
      code
      data { id }
      ${ERRORS}
    }
  }
`;

export interface ReceiptIssuer {
  id?: string;
  accountId?: string;
  /** The page this overrides the account for; null = the account's own details. */
  organizerProfileId?: string | null;
  regime?: ReceiptRegime | string;
  legalName?: string | null;
  registrationNumber?: string | null;
  streetAddress?: string | null;
  extraAddress?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  postalCode?: string | null;
  country?: string | null;
  issueLocation?: string | null;
  signatoryName?: string | null;
  signatoryTitle?: string | null;
  signatureUploadedFileId?: string | null;
  signatureUploadedFile?: { id?: string; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  receiptPrefix?: string | null;
  ownSeries?: boolean;
  footerText?: string | null;
  /** What the regime still needs before official receipts ([] = complete). */
  missing?: string[];
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export type ReceiptIssuerInput = Omit<ReceiptIssuer, "id" | "accountId" | "missing" | "status" | "rights" | "createdAt" | "updatedAt" | "signatureUploadedFile">;

/** Receipt details — the account's, and the per-page overrides. */
export class ReceiptIssuerService {
  static async list(filter?: { id?: string[]; organizerProfileId?: string[] }, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, { filter: filter || {}, params: { pagination: pagination || undefined, sortBy: sortBy || undefined } });
  }

  /**
   * EVERY row of the account — the account's and one per page with an override. ⓘ A whole set by nature (at most one per
   * organizer page), read whole on purpose: the page shows them all, there is no list to page.
   */
  static async listAll(): Promise<OpResult> {
    return listAllPages<ReceiptIssuer>(ReceiptIssuerService.list, {}, [{ name: "createdAt", order: "ASC" }]);
  }

  static async create(params: ReceiptIssuerInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(id: string, params: ReceiptIssuerInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id: [id] }, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id: [id] } });
  }
}

/** Human names for the fields `missing` lists, for "Still needed: …". */
export const RECEIPT_ISSUER_FIELD_LABEL: Record<string, string> = {
  legalName: "Legal name",
  registrationNumber: "Registration number",
  streetAddress: "Street address",
  city: "City",
  stateProvince: "Province or state",
  postalCode: "Postal code",
  country: "Country",
  issueLocation: "Where receipts are issued",
  signatoryName: "Who signs",
  signatureUploadedFileId: "Signature",
};
